import * as React from 'react';
import { ReactNode, useId } from 'react';
import { styled } from 'styled-components';
import Icon from './Icon';

interface CheckboxFieldProps {
	label: ReactNode;
	checked: boolean;
	onChange?: (checked: boolean) => void;
	onClick?: (e: React.MouseEvent<HTMLInputElement | HTMLSpanElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLSpanElement>) => void;
	id?: string;
	name?: string;
	className?: string;
	dataQaId?: string;
	// For accessibility
	'aria-checked'?: boolean;
	'aria-label'?: string;
}

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
	border: 0;
	clip: rect(0 0 0 0);
	clippath: inset(50%);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	white-space: nowrap;
	width: 1px;
`;

const CheckboxVisual = styled.span<CheckboxVisualProps>`
	display: flex;
	flex: 0 0 auto;
	width: 20px;
	height: 20px;
	border: 1px solid var(--Grey-40, #d3d3d3);
	border-radius: 4px;
	align-items: center;
	justify-content: center;
	position: relative;
	background: ${({ $checked }) => ($checked ? 'var(--Primary-squla-dark-blue, #0F0F41)' : 'white')};
	border-color: ${({ $checked }) =>
		$checked ? 'var(--Primary-squla-dark-blue, #0F0F41)' : 'var(--Grey-40, #d3d3d3)'};
	outline: none;
`;

const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 400;
	cursor: pointer;
	position: relative;

	/* Show focus ring on label when input is focused */
	${HiddenCheckbox}:focus-visible + ${CheckboxVisual} {
		outline: 3px solid var(--Red-80, #DF3522);
		outline-offset: 2px;
	}
`;

interface CheckboxVisualProps {
	$checked: boolean;
}

const CheckIcon = styled(Icon)`
	display: inline-flex;
`;

const LabelText = styled.span`
	flex: 1 1 0;
	color: inherit;
`;

export function CheckboxField({
	ref,
	label,
	checked,
	onChange,
	onClick,
	onBlur,
	onKeyDown,
	id,
	className,
	dataQaId,
	name,
}: CheckboxFieldProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
	const generatedId = useId();
	const checkboxId = id ?? generatedId;

	return (
		<CheckboxLabel htmlFor={checkboxId} className={className} data-qa-id={dataQaId}>
			<HiddenCheckbox
				ref={ref}
				id={checkboxId}
				name={name}
				checked={checked}
				aria-checked={checked}
				aria-label={typeof label === 'string' ? label : undefined}
				onChange={e => {
					if (onChange) {
						onChange(e.target.checked);
					}
				}}
				onClick={onClick}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
			/>
			<CheckboxVisual $checked={checked} aria-hidden="true">
				{checked && <CheckIcon name="check-white" aria-hidden="true" color="#fff" />}
			</CheckboxVisual>
			<LabelText aria-hidden="true">{label}</LabelText>
		</CheckboxLabel>
	);
}

export type { CheckboxFieldProps };
export default CheckboxField;
